/**
 * Modes Dialog Component
 *
 * Full-page dialog for viewing, searching, filtering, and managing image modes.
 * Modes configure all 3 panels at once. Mirrors the ImagePresetsDialog pattern.
 */

import { useState, useMemo, useCallback } from 'react';
import { Layers, Search, Settings2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ModeCard } from './ModeCard';
import { ManageTagsCategoriesDialog } from './ManageTagsCategoriesDialog';
import type { TagDefinition } from '../types/image-generator.types';
import type { ModelDefinitionListItem } from '@/lib/api';
import type { ImageMode } from '../types/image-generator-modes';

export interface ModesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modes: ImageMode[];
  tagDefinitions: TagDefinition[];
  availableModels: ModelDefinitionListItem[];
  onLoadMode: (mode: ImageMode) => void;
  onDeleteMode: (modeId: string) => void;
  onUpdateMode: (modeId: string, updates: Partial<Pick<ImageMode, 'tags'>>) => void;
  onSaveMode: (name: string) => void;
  onAddTag: (name: string) => string;
  onDeleteTag: (tagId: string) => void;
  onRenameTag: (tagId: string, name: string) => void;
}

export function ModesDialog({
  open,
  onOpenChange,
  modes,
  tagDefinitions,
  availableModels,
  onLoadMode,
  onDeleteMode,
  onUpdateMode,
  onSaveMode,
  onAddTag,
  onDeleteTag,
  onRenameTag,
}: ModesDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newModeName, setNewModeName] = useState('');

  const toggleTagFilter = (tagId: string) => {
    setSelectedTagFilters((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );
  };

  const builtInModes = useMemo(() => modes.filter((m) => m.builtIn), [modes]);
  const customModes = useMemo(() => modes.filter((m) => !m.builtIn), [modes]);

  const filterModes = useCallback(
    (modesToFilter: ImageMode[]) => {
      let result = modesToFilter;

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        result = result.filter((mode) => {
          if (mode.name.toLowerCase().includes(query)) return true;
          if (mode.description.toLowerCase().includes(query)) return true;
          const modeTagNames = tagDefinitions
            .filter((t) => mode.tags.includes(t.id))
            .map((t) => t.name.toLowerCase());
          if (modeTagNames.some((name) => name.includes(query))) return true;
          return false;
        });
      }

      if (selectedTagFilters.length > 0) {
        result = result.filter((m) => selectedTagFilters.every((tagId) => m.tags.includes(tagId)));
      }

      return result;
    },
    [searchQuery, selectedTagFilters, tagDefinitions],
  );

  const filteredBuiltIn = useMemo(() => filterModes(builtInModes), [filterModes, builtInModes]);
  const filteredCustom = useMemo(() => filterModes(customModes), [filterModes, customModes]);
  const totalFiltered = filteredBuiltIn.length + filteredCustom.length;

  const handleSaveMode = () => {
    const trimmed = newModeName.trim();
    if (!trimmed) return;
    onSaveMode(trimmed);
    setNewModeName('');
    setIsSaving(false);
  };

  const handleCreateTag = (name: string): string => {
    return onAddTag(name);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-lg flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Modes
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Modes set the model and presets for all image panels
                </p>
              </div>
              <div className="flex items-center gap-2 mr-8">
                {!isSaving ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsSaving(true)}
                    className="gap-1.5"
                  >
                    <Save className="h-4 w-4" />
                    Save Current
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      value={newModeName}
                      onChange={(e) => setNewModeName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveMode();
                        }
                        if (e.key === 'Escape') {
                          setIsSaving(false);
                          setNewModeName('');
                        }
                      }}
                      placeholder="Mode name..."
                      className="h-8 w-48 text-sm"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      onClick={handleSaveMode}
                      disabled={!newModeName.trim()}
                      className="h-8"
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsSaving(false);
                        setNewModeName('');
                      }}
                      className="h-8"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsManageOpen(true)}
                  className="gap-1.5"
                >
                  <Settings2 className="h-4 w-4" />
                  Manage Tags
                </Button>
              </div>
            </div>

            {/* Search */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search modes by name, description, or tag..."
                className="pl-9"
              />
            </div>

            {/* Tag filter */}
            {tagDefinitions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tagDefinitions.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant={selectedTagFilters.includes(tag.id) ? 'default' : 'outline'}
                    className="cursor-pointer text-xs"
                    onClick={() => toggleTagFilter(tag.id)}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            )}
          </DialogHeader>

          {/* Body: scrollable grid */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {modes.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <Layers className="h-16 w-16 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No modes available</p>
              </div>
            ) : totalFiltered === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <Search className="h-16 w-16 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No modes match your filters</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Built-in modes */}
                {filteredBuiltIn.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">Built-in</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {filteredBuiltIn.map((mode) => (
                        <ModeCard
                          key={mode.id}
                          mode={mode}
                          tagDefinitions={tagDefinitions}
                          availableModels={availableModels}
                          onLoadMode={onLoadMode}
                          onDeleteMode={onDeleteMode}
                          onUpdateMode={onUpdateMode}
                          onCreateTag={handleCreateTag}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom modes */}
                {filteredCustom.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">Custom</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {filteredCustom.map((mode) => (
                        <ModeCard
                          key={mode.id}
                          mode={mode}
                          tagDefinitions={tagDefinitions}
                          availableModels={availableModels}
                          onLoadMode={onLoadMode}
                          onDeleteMode={onDeleteMode}
                          onUpdateMode={onUpdateMode}
                          onCreateTag={handleCreateTag}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Nested manage dialog */}
      <ManageTagsCategoriesDialog
        open={isManageOpen}
        onOpenChange={setIsManageOpen}
        tagDefinitions={tagDefinitions}
        onAddTag={onAddTag}
        onDeleteTag={onDeleteTag}
        onRenameTag={onRenameTag}
      />
    </>
  );
}
